import * as React from "react"

import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"
import { ChevronDownIcon } from "../ChevronDownIcon/ChevronDownIcon"

/* ── Context ──────────────────────────────────────────────── */

interface NestedSelectContextValue {
  value: string | undefined
  label: string | undefined
  onSelect: (value: string, label: string) => void
}

const NestedSelectContext = React.createContext<NestedSelectContextValue>({
  value: undefined,
  label: undefined,
  onSelect: () => {},
})

/* ── Variants ─────────────────────────────────────────────── */

const nestedSelectTriggerVariants = cva([
  "coval-field flex h-control w-full cursor-pointer items-center justify-between gap-2 rounded-md px-2",
  "text-sm text-coval-text",
  "bg-surface-default",
  "transition-colors duration-150",
  "hover:bg-surface-contrast disabled:hover:bg-surface-default",
  "border border-surface-1",
  "disabled:cursor-default disabled:opacity-50",
])

const nestedSelectContentVariants = cva([
  "z-50 max-h-72 min-w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto rounded-md p-1",
  "bg-surface-default text-coval-text",
  "border border-coval-border shadow-md",
])

/* ── Root ─────────────────────────────────────────────────── */

export interface NestedSelectProps {
  value?: string
  defaultValue?: string
  initialLabel?: string
  onValueChange?: (value: string) => void
  children: React.ReactNode
}

function NestedSelect({
  value: controlledValue,
  defaultValue,
  initialLabel,
  onValueChange,
  children,
}: NestedSelectProps) {
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue)
  const [label, setLabel] = React.useState<string | undefined>(initialLabel)

  React.useEffect(() => {
    if (initialLabel !== undefined) {
      setLabel(initialLabel)
    }
  }, [initialLabel])

  const isControlled = controlledValue !== undefined
  const value = isControlled ? controlledValue : uncontrolledValue

  const onSelect = React.useCallback(
    (newValue: string, newLabel: string) => {
      if (!isControlled) {
        setUncontrolledValue(newValue)
      }
      setLabel(newLabel)
      onValueChange?.(newValue)
    },
    [isControlled, onValueChange],
  )

  const ctx = React.useMemo(
    () => ({ value, label, onSelect }),
    [value, label, onSelect],
  )

  return (
    <NestedSelectContext.Provider value={ctx}>
      <DropdownMenuPrimitive.Root>{children}</DropdownMenuPrimitive.Root>
    </NestedSelectContext.Provider>
  )
}

/* ── Trigger ──────────────────────────────────────────────── */

export interface NestedSelectTriggerProps
  extends
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger>,
    VariantProps<typeof nestedSelectTriggerVariants> {
  error?: boolean
}

const NestedSelectTrigger = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Trigger>,
  NestedSelectTriggerProps
>(({ className, error, children, ...props }, ref) => (
  <DropdownMenuPrimitive.Trigger
    ref={ref}
    data-invalid={error || undefined}
    className={cn(
      nestedSelectTriggerVariants(),
      error &&
        "border-coval-danger-border hover:border-coval-danger-border focus:border-coval-danger-border bg-coval-danger-surface text-coval-danger-on-surface hover:bg-coval-danger-surface disabled:hover:bg-coval-danger-surface [&_[data-placeholder]]:text-coval-danger-on-surface/80",
      className,
    )}
    {...props}
  >
    <span className="min-w-0 flex-1 truncate text-left">{children}</span>
    <ChevronDownIcon />
  </DropdownMenuPrimitive.Trigger>
))
NestedSelectTrigger.displayName = "NestedSelectTrigger"

/* ── Value ────────────────────────────────────────────────── */

export interface NestedSelectValueProps {
  placeholder?: string
  className?: string
}

function NestedSelectValue({ placeholder, className }: NestedSelectValueProps) {
  const { label } = React.useContext(NestedSelectContext)

  return (
    <span
      data-placeholder={label ? undefined : ""}
      className={cn("truncate text-sm", !label && "text-coval-text", className)}
    >
      {label ?? placeholder}
    </span>
  )
}

/* ── Content ──────────────────────────────────────────────── */

const NestedSelectContent = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, align = "start", ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      align={align}
      className={cn(nestedSelectContentVariants(), className)}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
))
NestedSelectContent.displayName = "NestedSelectContent"

/* ── Item ─────────────────────────────────────────────────── */

export interface NestedSelectItemProps extends Omit<
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>,
  "onSelect"
> {
  value: string
}

const NestedSelectItem = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Item>,
  NestedSelectItemProps
>(({ className, value: itemValue, children, ...props }, ref) => {
  const { value, onSelect } = React.useContext(NestedSelectContext)
  const isSelected = value === itemValue

  // A radio item rather than a plain one, so the selection is exposed and not
  // only drawn as a check mark.
  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      role="menuitemradio"
      aria-checked={isSelected}
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm py-1.5 px-2",
        "text-sm text-coval-text",
        "outline-none",
        "hover:bg-surface-contrast focus:bg-surface-contrast",
        isSelected && "bg-surface-contrast",
        "data-[disabled]:cursor-default data-[disabled]:opacity-50 data-[disabled]:hover:bg-transparent",
        className,
      )}
      onSelect={() => {
        const label = typeof children === "string" ? children : itemValue
        onSelect(itemValue, label)
      }}
      {...props}
    >
      {children}
      {isSelected && (
        <span className="ml-auto flex h-3.5 w-3.5 shrink-0 items-center justify-center">
          <CheckIcon />
        </span>
      )}
    </DropdownMenuPrimitive.Item>
  )
})
NestedSelectItem.displayName = "NestedSelectItem"

/* ── Label ────────────────────────────────────────────────── */

const NestedSelectLabel = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      "px-2 py-1.5 text-sm font-medium text-coval-text-muted",
      className,
    )}
    {...props}
  />
))
NestedSelectLabel.displayName = "NestedSelectLabel"

/* ── Group ────────────────────────────────────────────────── */

const NestedSelectGroup = DropdownMenuPrimitive.Group

/* ── Separator ────────────────────────────────────────────── */

const NestedSelectSeparator = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-coval-border", className)}
    {...props}
  />
))
NestedSelectSeparator.displayName = "NestedSelectSeparator"

/* ── Sub (nested menu) ────────────────────────────────────── */

const NestedSelectSub = DropdownMenuPrimitive.Sub

const NestedSelectSubTrigger = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      "flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 px-2",
      "text-sm text-coval-text",
      "outline-none",
      "hover:bg-surface-contrast focus:bg-surface-contrast",
      "data-[state=open]:bg-surface-contrast",
      className,
    )}
    {...props}
  >
    {children}
    <span className="ml-auto">
      <ChevronRightIcon />
    </span>
  </DropdownMenuPrimitive.SubTrigger>
))
NestedSelectSubTrigger.displayName = "NestedSelectSubTrigger"

const NestedSelectSubContent = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.SubContent
      ref={ref}
      className={cn(nestedSelectContentVariants(), className)}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
))
NestedSelectSubContent.displayName = "NestedSelectSubContent"

/* ── Inline icons ─────────────────────────────────────────── */

function ChevronRightIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M4.5 3L7.5 6L4.5 9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M2.5 6L5 8.5L9.5 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/* ── Exports ──────────────────────────────────────────────── */

export {
  NestedSelect,
  NestedSelectTrigger,
  NestedSelectValue,
  NestedSelectContent,
  NestedSelectItem,
  NestedSelectLabel,
  NestedSelectGroup,
  NestedSelectSeparator,
  NestedSelectSub,
  NestedSelectSubTrigger,
  NestedSelectSubContent,
  nestedSelectTriggerVariants,
  nestedSelectContentVariants,
}
